// Share-link state machine (architecture sprint-06 §3.3). One source of truth for which actions each
// derived status allows, used by the server (authoritative, inside the row-locked transaction) and the
// client (which buttons to render). Dependency-free (shared-purity.test.ts).

/** Derived status, unchanged rule (sprint-05 §3.2): revoked > expired (expires_at <= now) > active. */
export type ShareLinkStatusName = 'active' | 'expired' | 'revoked';
export type ShareLinkAction = 'copy' | 'extend' | 'visibility' | 'revoke';

export const SHARE_LINK_ACTIONS: Readonly<Record<ShareLinkStatusName, readonly ShareLinkAction[]>> = {
  active: ['copy', 'extend', 'visibility', 'revoke'],
  // Extend revives an expired link (approved decision 3).
  expired: ['extend'],
  // Revoked is final: not even Copy.
  revoked: [],
};

export function canDo(status: ShareLinkStatusName, action: ShareLinkAction): boolean {
  return SHARE_LINK_ACTIONS[status].includes(action);
}

/** The app-wide upper date bound; S-8 sets no other maximum for an expiry. */
export const MAX_EXTEND_DATE = '2099-12-31';
/** Default offered by the Extend picker: the acting Admin's today + this many days. */
export const DEFAULT_EXTEND_DAYS = 30;

const LOCAL_DATE_SHAPE = /^(\d{4})-(\d{2})-(\d{2})$/;

function isRealDate(value: string): boolean {
  const match = LOCAL_DATE_SHAPE.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const probe = new Date(Date.UTC(year, month - 1, day));
  return probe.getUTCFullYear() === year && probe.getUTCMonth() === month - 1 && probe.getUTCDate() === day;
}

/** Extend picker rule: any real date from the acting Admin's today (inclusive) to 2099-12-31. */
export function isValidExtendDate(expiresOn: string, todayInAdminTz: string): boolean {
  if (!isRealDate(expiresOn)) return false;
  // YYYY-MM-DD strings compare in calendar order.
  return expiresOn >= todayInAdminTz && expiresOn <= MAX_EXTEND_DATE;
}

/** Exact Make public confirmation wording (brief). rangeLabel e.g. '1 Oct – 31 Oct 2026'. */
export function publicConfirmText(rangeLabel: string): string {
  return `Anyone with this URL will see named employees' hours for ${rangeLabel}, without signing in, until it expires or you revoke it or make it private.`;
}
